/**
 * Markdown Extensions - CodeMirror 6 Markdown 擴展
 *
 * 提供 Markdown 語法高亮、自動格式化、鍵盤快捷鍵
 *
 * @module @linkode/web/editor/cm/markdownExtensions
 */

import { Extension, Prec } from '@codemirror/state';
import { EditorView, keymap, KeyBinding } from '@codemirror/view';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import {
  defaultHighlightStyle,
  syntaxHighlighting,
  indentOnInput,
  bracketMatching,
  HighlightStyle,
} from '@codemirror/language';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { tags } from '@lezer/highlight';
import { createEmojiDecoration } from './emojiDecoration';

// ============================================================================
// Types
// ============================================================================

/**
 * Markdown 擴展配置
 */
export interface MarkdownExtensionConfig {
  /** 啟用語法高亮 */
  syntaxHighlighting: boolean;
  /** 啟用自動格式化 */
  autoFormatting: boolean;
  /** 啟用鍵盤快捷鍵 */
  keyboardShortcuts: boolean;
  /** 啟用行號 */
  lineNumbers?: boolean;
}

/**
 * 預設配置
 */
export const defaultMarkdownConfig: MarkdownExtensionConfig = {
  syntaxHighlighting: true,
  autoFormatting: true,
  keyboardShortcuts: true,
  lineNumbers: false,
};

// ============================================================================
// Markdown Highlight Style
// ============================================================================

/**
 * 自訂 Markdown 高亮樣式
 * 支援所有 Markdown 元素：heading, list, bold, italic, quote, code block, inline code, link, image
 */
export const markdownHighlightStyle = HighlightStyle.define([
  // Headings (H1-H6)
  { tag: tags.heading1, fontWeight: 'bold', fontSize: '1.6em' },
  { tag: tags.heading2, fontWeight: 'bold', fontSize: '1.4em' },
  { tag: tags.heading3, fontWeight: 'bold', fontSize: '1.2em' },
  { tag: tags.heading4, fontWeight: 'bold', fontSize: '1.1em' },
  { tag: tags.heading5, fontWeight: 'bold', fontSize: '1.05em' },
  { tag: tags.heading6, fontWeight: 'bold', fontSize: '1em' },

  // Emphasis
  { tag: tags.emphasis, fontStyle: 'italic' },
  { tag: tags.strong, fontWeight: 'bold' },

  // Code
  { tag: tags.monospace, fontFamily: 'monospace' },

  // Links and URLs
  { tag: tags.link, textDecoration: 'underline' },
  { tag: tags.url, textDecoration: 'underline' },

  // Quote
  { tag: tags.quote, fontStyle: 'italic', color: '#666' },

  // List markers
  { tag: tags.list, color: '#666' },

  // Meta (frontmatter, etc.)
  { tag: tags.meta, color: '#888' },

  // Processing instruction (code fence markers)
  { tag: tags.processingInstruction, color: '#888' },
]);

// ============================================================================
// Keyboard Commands
// ============================================================================

/**
 * 切換粗體格式
 * 在選取文字前後加上 ** 或移除
 */
export function toggleBold(view: EditorView): boolean {
  return toggleMarkdownFormat(view, '**', '**');
}

/**
 * 切換斜體格式
 * 在選取文字前後加上 * 或移除
 */
export function toggleItalic(view: EditorView): boolean {
  return toggleMarkdownFormat(view, '*', '*');
}

/**
 * 插入連結
 * 將選取文字轉換為 [text](url) 格式
 */
export function insertLink(view: EditorView): boolean {
  const { state } = view;
  const { from, to } = state.selection.main;
  const selectedText = state.sliceDoc(from, to);

  const linkText = selectedText || 'link text';
  const replacement = `[${linkText}](url)`;

  view.dispatch({
    changes: { from, to, insert: replacement },
    // 選取 url 部分以便使用者直接輸入
    selection: {
      anchor: from + linkText.length + 3,
      head: from + linkText.length + 6,
    },
  });

  return true;
}

/**
 * 通用 Markdown 格式切換函式
 */
function toggleMarkdownFormat(view: EditorView, prefix: string, suffix: string): boolean {
  const { state } = view;
  const { from, to } = state.selection.main;
  const selectedText = state.sliceDoc(from, to);

  // 檢查是否已經有格式
  const hasFormat = selectedText.startsWith(prefix) && selectedText.endsWith(suffix);

  if (hasFormat && selectedText.length >= prefix.length + suffix.length) {
    // 移除格式
    const newText = selectedText.slice(prefix.length, -suffix.length);
    view.dispatch({
      changes: { from, to, insert: newText },
      selection: { anchor: from, head: from + newText.length },
    });
  } else {
    // 檢查選取範圍外是否有格式標記
    const beforeText = state.sliceDoc(Math.max(0, from - prefix.length), from);
    const afterText = state.sliceDoc(to, to + suffix.length);

    if (beforeText === prefix && afterText === suffix) {
      // 移除外部格式
      view.dispatch({
        changes: [
          { from: from - prefix.length, to: from, insert: '' },
          { from: to, to: to + suffix.length, insert: '' },
        ],
        selection: {
          anchor: from - prefix.length,
          head: to - prefix.length,
        },
      });
    } else {
      // 添加格式
      const newText = `${prefix}${selectedText}${suffix}`;
      view.dispatch({
        changes: { from, to, insert: newText },
        selection: {
          anchor: from + prefix.length,
          head: from + prefix.length + selectedText.length,
        },
      });
    }
  }

  return true;
}

// ============================================================================
// Auto Formatting
// ============================================================================

/**
 * 自動格式化擴展
 * 處理 #、-、``` 等自動格式化
 */
export function createAutoFormatExtension(): Extension {
  return EditorView.inputHandler.of((view, from, to, text) => {
    // 只處理單字元輸入
    if (text.length !== 1) return false;

    const line = view.state.doc.lineAt(from);
    const lineStart = line.from;
    const textBeforeCursor = view.state.sliceDoc(lineStart, from);

    // # + space -> heading
    if (text === ' ' && /^#{1,6}$/.test(textBeforeCursor)) {
      // 讓預設行為處理，Markdown 語法高亮會自動識別
      return false;
    }

    // - or * + space -> list item
    if (text === ' ' && /^[-*]$/.test(textBeforeCursor)) {
      // 讓預設行為處理
      return false;
    }

    // ``` + newline -> code block
    if (text === '\n' || text === '\r') {
      const fullLineText = view.state.sliceDoc(lineStart, to);
      if (/^```\w*$/.test(fullLineText.trim())) {
        // 插入程式碼區塊結構
        const indent = fullLineText.match(/^\s*/)?.[0] || '';
        const insertion = `\n${indent}\n${indent}\`\`\``;

        view.dispatch({
          changes: { from: to, to, insert: insertion },
          selection: { anchor: to + 1 + indent.length },
        });
        return true;
      }
    }

    return false;
  });
}

// ============================================================================
// Keymap
// ============================================================================

/**
 * Markdown 專用鍵盤快捷鍵
 */
export const markdownKeymap: readonly KeyBinding[] = [
  // 格式化快捷鍵
  { key: 'Mod-b', run: toggleBold, preventDefault: true },
  { key: 'Mod-i', run: toggleItalic, preventDefault: true },
  { key: 'Mod-k', run: insertLink, preventDefault: true },
];

// ============================================================================
// Main Extension Factory
// ============================================================================

/**
 * 建立 Markdown 擴展集
 *
 * @param config - 擴展配置
 * @returns CodeMirror 擴展陣列
 *
 * @example
 * ```typescript
 * const extensions = createMarkdownExtensions({
 *   syntaxHighlighting: true,
 *   autoFormatting: true,
 *   keyboardShortcuts: true,
 * });
 * ```
 */
export function createMarkdownExtensions(
  config: Partial<MarkdownExtensionConfig> = {}
): Extension[] {
  const mergedConfig: MarkdownExtensionConfig = {
    ...defaultMarkdownConfig,
    ...config,
  };

  const extensions: Extension[] = [];

  // 基礎 Markdown 語言支援
  extensions.push(
    markdown({
      base: markdownLanguage,
    })
  );

  // 語法高亮
  if (mergedConfig.syntaxHighlighting) {
    extensions.push(
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      syntaxHighlighting(markdownHighlightStyle)
    );
  }

  // 自動格式化
  if (mergedConfig.autoFormatting) {
    extensions.push(createAutoFormatExtension(), indentOnInput(), bracketMatching());
  }

  // 鍵盤快捷鍵
  if (mergedConfig.keyboardShortcuts) {
    // 使用 Prec.high 確保 Markdown 快捷鍵優先
    extensions.push(
      Prec.high(keymap.of(markdownKeymap)),
      keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
      history()
    );
  }

  // 基礎編輯器設定
  extensions.push(
    EditorView.lineWrapping,
    EditorView.contentAttributes.of({
      'aria-label': 'Markdown editor',
      role: 'textbox',
      'aria-multiline': 'true',
    })
  );

  // Emoji Twemoji 裝飾
  extensions.push(createEmojiDecoration());

  return extensions;
}

// ============================================================================
// Exports
// ============================================================================

export { markdown, markdownLanguage } from '@codemirror/lang-markdown';
